<script lang="ts">
							import SectionComponent from '$lib/section/SectionComponent.svelte';
							import copy from "copy-to-clipboard"; 
							import { onMount } from "svelte";
							import PrevNextSection from '$lib/prevNext/PrevNextSection.svelte';

							interface ComponentProps {
								setVisible: (id: string, v: boolean) => void;
							}
							let { setVisible }: ComponentProps = $props();
							
							const visibleById = $state<Record<string, boolean>>({});

							/**
               * This function will go through all the 'pre' elements
               * on the page and add a copy button to them.
               * Thanks to: https://slavbasharov.com/blog/adding-click-to-copy-code-markdown-blog
               */
              onMount(() => {
                  const codeBlocks = document.querySelectorAll("pre");
                  codeBlocks.forEach((block) => {
                      const copyPrompt = document.createElement("div");
                      copyPrompt.className = "copy-prompt";
                      const copyPromptText = document.createElement("p");
                      copyPromptText.innerHTML = "👆 Click to copy";
                      copyPromptText.className = 'copy-prompt-p';
                      const copyIcon = document.createElement("img");
                      copyIcon.src = "/icons/copy-icon.svg";
                      copyIcon.className = "copy-prompt-img";
                      copyPrompt.appendChild(copyIcon);
                      copyPrompt.appendChild(copyPromptText);
                      block.appendChild(copyPrompt);
											block.querySelector('.copy-prompt > p')?.addEventListener('click', () => {
												if (block.querySelector('code')?.textContent !== null && block.querySelector('code')?.textContent !== undefined) {
													copy(block.querySelector('code')!.textContent!);
												}
												if (block.querySelector('.copy-prompt > p')?.innerHTML !== null && block.querySelector('.copy-prompt > p')?.innerHTML !== undefined) {
													block.querySelector('.copy-prompt > p')!.innerHTML = 'Copied!';
													setTimeout(() => {
														block.querySelector('.copy-prompt > p')!.innerHTML = '👆 Click to copy';
													}, 1000);
												}
											});
                  });
              });
		let prevLink= '/Tutorial/In_Need_of_Scoping';
    let nextLink= '/Tutorial/How_to_Handle_Typing';
    
    import Figure from '$lib/figures/Figure.svelte';
</script>

<PrevNextSection {prevLink} {nextLink} />



<SectionComponent tag="h1" 
  id="more-scoping-1"
  bind:intersecting={visibleById["more-scoping-1"]}
  onIntersectChange={(visible) => setVisible("more-scoping-1", visible)}
>More Scoping</SectionComponent>
<p>In this lesson, you’ll learn more about scoping. But first, we’ll add the metamodel
and the projections for the fourth model unit — the <strong>Tests</strong>.</p>
<SectionComponent tag="h2" 
  id="the-test-model-unit-metamodel-2"
  bind:intersecting={visibleById["the-test-model-unit-metamodel-2"]}
  onIntersectChange={(visible) => setVisible("the-test-model-unit-metamodel-2", visible)}
>The <em>Test</em> model unit metamodel</SectionComponent>
<p>In the requirements of our DSL in the <a href="/Tutorial/Overview">introduction</a>, we stated that<br>
“… our client company wants a means to test the page flow before the actual webpages are produced.”<br>
The <strong>Test</strong> model unit is the starting point for fulfilling that requirement.</p>
<p>In a Test, a teacher can add multiple scenarios, each describing a series of page flows.<br>
For instance, the teacher might want to test that from the start page <code>Theory101</code>, a pupil who answers all questions correctly is directed to <code>InDepth101</code>,<br>
and after succeeding there, proceeds to <code>Theory102</code>.</p>
<p>Within a test, we need to specify both the answers given to questions on the current page and the expected follow-up page.<br>
Add the following to the file <code>edu-tests.ast</code>:</p>
<pre class="language-freon"><code class="language-freon">// Education/lesson7-defs/edu-tests.ast

language Education

modelunit Test &#123;
    name: identifier;
    scenarios: Scenario[];
    reference main: SiteGroup;
    reference flow: Flow;
&#125;

concept Scenario &#123;
    description: string;
    testFlow: TestFlow[];
&#125;

concept TestFlow &#123;
    steps: Step[]; /* Note that the order is of importance */
&#125;

concept Step &#123;
    reference fromPage: Page;
    answerSeries: Answer[];
&#125;

concept LastStep base Step &#123;
&#125;

concept Answer &#123;
    reference question: Question;
    value: NumberConcept;
&#125;</code></pre>
<SectionComponent tag="h2" 
  id="the-test-model-unit-projections-3"
  bind:intersecting={visibleById["the-test-model-unit-projections-3"]}
  onIntersectChange={(visible) => setVisible("the-test-model-unit-projections-3", visible)}
>The <em>Test</em> model unit projections</SectionComponent>
<p>We’ll also define projections for these concepts. By now, none of this should look unfamiliar:</p>
<pre class="language-freon"><code class="language-freon">// Education/lesson7-defs/edu-tests.edit

editor default

Test &#123;[
    Test $&#123;self.name&#125;
    Regarding MainTopic: $&#123;self.main&#125;, and flow: $&#123;self.flow&#125;

    $&#123;self.scenarios&#125;
]&#125;

Scenario &#123;[
    ------------------------------------
    $&#123;self.description&#125;

        $&#123;self.testFlow vertical&#125;
]&#125;

TestFlow &#123;[
    $&#123;self.steps horizontal separator[===&gt;]&#125;
]&#125;

Step &#123;[
    $&#123;self.fromPage&#125; =&gt; $&#123;self.answerSeries table rows&#125;
]&#125;

LastStep &#123;[
    $&#123;self.fromPage&#125;
]&#125;

Answer &#123; table [
    $&#123;self.question&#125; | $&#123;self.value&#125;
]&#125;</code></pre>
<SectionComponent tag="h2" 
  id="namespace-addition-4"
  bind:intersecting={visibleById["namespace-addition-4"]}
  onIntersectChange={(visible) => setVisible("namespace-addition-4", visible)}
>Namespace Addition</SectionComponent>
<p>Now comes the interesting part.<br>
If you try to build a test flow, you’ll notice that when adding answers, the user can still access question names from other pages.<br>
Generate the editor, open the model <code>lesson6</code>, and in the side panel select <code>TestB</code> to see something like this:</p>
<Figure
imageName={'tutorial/Tutorial-lesson7-screenshot1.png'}
caption={'No scoping in the Test model unit'}
figureNumber={1}
/>
<p>Here, the questions <code>pie</code> and <code>pie2</code> belong to the page <code>Theory101</code>, but <code>difficult1</code> and <code>further</code> do not.<br>
How can we prevent such mistakes?</p>
<p>The answer lies in <strong>another scope rule</strong>.<br>
We need to draw a boundary around each <code>Step</code> instance — a namespace that only includes the relevant names.<br>
So, we’ll make <code>Step</code> a namespace. However, this namespace itself has no direct names; the question names we want belong to the <code>fromPage</code> property.<br>
To tell Freon to include those, we use a <strong>namespace addition</strong>.</p>
<p>A namespace addition imports names into a namespace, as long as those names can be directly reached from the namespace object.<br>
In our case, <code>Step</code> can use names from its <code>fromPage</code>, but not from other scenarios.</p>
<pre class="language-freon"><code class="language-freon">// Education/lesson7-defs/edu.scope

scoper for language Education

isNamespace &#123; Page, Step &#125;

Step &#123;
    imports &#123;
        self.fromPage;
    &#125; 
&#125;</code></pre>
<p>Now regenerate and take another look.<br>
You’ll see that the available question names in each answer series are <strong>only</strong> those from the corresponding <code>fromPage</code>.</p>
<p>Yes — scoping really can be this easy!</p>
<p>In the next lesson, we’ll take things a step further by introducing Freon’s <strong>typing system</strong> to help users build even more reliable models.</p>


<PrevNextSection {prevLink} {nextLink} />